/**
 * App.tsx now acts as a thin entry point:
 * 1. Configures Amplify with `awsConfig`.
 * 2. Wraps the entire tree in `AppProviders`, which nests the QueryClient,
 *    Redux store/persist gate, Tooltip setup, Authenticator provider,
 *    PostHog, feature flags, view settings, focus mode, agent chat,
 *    toaster, and the auth error boundary.
 * 3. Renders `AuthGate`, which waits for Amplify auth status, then
 *    mounts either `AppContent` (authenticated shell) or the public
 *    auth routes. `AppContent` lives in `src/app/AppContent.tsx` and
 *    composes all routing/layout logic from the new modules.
 *
 * Truly public routes (no auth required, no redirect) are rendered via
 * PublicRouteGuard before AuthGate so they work regardless of login state.
 */
import '@aws-amplify/ui-react/styles.css';
import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';
import { useLocation } from 'react-router-dom';
import { AppProviders } from './app/AppProviders';
import { AuthGate } from './app/AuthGate';
import { ReadOnlyBanner } from './components/auth/ReadOnlyBanner';
import FallbackSpinner from './components/FallbackSpinner';
import { UpdateNotification } from './components/UpdateNotification';
import { awsConfig } from './config/aws-config';

Amplify.configure(awsConfig);

cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    secure: window.location.protocol === 'https:',
    sameSite: 'strict',
    expires: 1,
    path: '/',
  })
);

const PUBLIC_PATHS = ['/thrive-check-full', '/thrive-check-mini', '/public'];

const AppRouter = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith('/thrive-check-full')) {
    window.location.replace('/thrive-check-full/index.html');
    return <FallbackSpinner />;
  }
  if (pathname.startsWith('/thrive-check-mini')) {
    window.location.replace('/thrive-check-mini/index.html');
    return <FallbackSpinner />;
  }
  if (pathname.startsWith('/public')) {
    window.location.replace('/public/index.html');
    return <FallbackSpinner />;
  }
  return <AuthGate />;
};

const App = () => (
  <AppProviders>
    <ReadOnlyBanner />
    <UpdateNotification />
    <AppRouter />
  </AppProviders>
);

export default App;
